import classes from '~/config/classes'

export default class HotspotsComponent extends HTMLElement {
  constructor() {
    super()
    this.initializeElements()
    this.currentIndex = 0
    this.points = JSON.parse(this.getAttribute('points'))
    this.autoRotate = this.getAttribute('auto-rotate') === 'true'
    this.speed = this.getAttribute('rotate-speed')
    this.cycleInterval = null
  }

  /**
   * Initializes elements.
   */
  initializeElements() {
    console.log('initializeElements, testing', classes)
    this.mediaElement = this.querySelector('[data-dots]')
    this.mediaContent = this.querySelectorAll('[data-details]')
    this.next = this.querySelector('[data-slider-next]')
    this.prev = this.querySelector('[data-slider-prev]')
    this.detailsWrapper = this.querySelector('[data-details-wrapper]')
    this.container = this.querySelector('[data-container]')
    this.poiElements = []

    // canvas
    this.canvas = document.createElement('canvas')
    this.canvas.classList.add(classes.desktop, 'hotspots__canvas')
    this.container.appendChild(this.canvas)
  }

  connectedCallback() {
    this.positionBoxes()
    this.renderPois()
    this.setupEventListeners()
    this.updateCanvas()
  }

  disconnectedCallback() {
    this.stopAutoRotating()
  }

  /**
   * Update the canvas size and redraw the lines.
   */
  updateCanvas() {
    this.canvas.width = this.container.clientWidth
    this.canvas.height = this.container.clientHeight
    this.drawLines()

    if (this.autoRotate) {
      this.stopAutoRotating()
      this.startAutoRotating()
    }
  }

  /**
   * Auto rotating for the hotspots.
   */
  startAutoRotating() {
    if (this.autoRotate && this.canvas.width > 917) {
      this.cycleInterval = setInterval(() => {
        this.handleNextPrevClick(true)
      }, this.calculateSpeed(this.speed))
    } else {
      this.stopAutoRotating()
    }
  }

  /**
   * Stops the auto rotating.
   */
  stopAutoRotating() {
    if (this.cycleInterval) {
      clearInterval(this.cycleInterval)
      this.cycleInterval = null
    }
  }

  /**
   * Turn the speed value into milliseconds.
   * @param {string} value - The speed value.
   * @returns {number} The speed in milliseconds.
   */
  calculateSpeed(value) {
    // eslint-disable-next-line radix
    return parseInt(value, 10) * 1000
  }

  /**
   * Sets up event listeners.
   */
  setupEventListeners() {
    window.addEventListener('resize', () => this.updateCanvas())
    this.next.addEventListener('click', (event) => {
      event.stopPropagation()
      this.handleNextPrevClick(true)
    })
    this.prev.addEventListener('click', (event) => {
      event.stopPropagation()
      this.handleNextPrevClick(false)
    })
    this.addEventListener('click', event => this.handlePoiClick(event))
  }

  /**
   * Handles click events on POIs.
   * @param {MouseEvent} event - The mouse event.
   */
  handlePoiClick(event) {
    const poiElement = event.target
    const poiId = poiElement?.dataset?.poiId
    this.stopAutoRotating()
    this.updateActiveState(poiId)

    const clickedIndex = this.points.findIndex(point => point.id === poiId)
    if (clickedIndex !== -1) {
      this.currentIndex = clickedIndex
    }
  }

  /**
   * Handles click events on next and previous buttons.
   * @param {boolean} isNext - Indicates if the next button was clicked.
   */
  handleNextPrevClick(isNext) {
    this.currentIndex = isNext
      ? (this.currentIndex + 1) % this.points.length
      : (this.currentIndex + this.points.length - 1) % this.points.length
    this.updateActiveState(this.points[this.currentIndex].id)
  }

  /**
   * Renders POIs icons on the media element.
   */
  renderPois() {
    const pointIcon = `
      <svg xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 46 46" fill="none">
        <circle opacity="0.5" cx="23" cy="23" r="22" stroke="currentColor" stroke-width="2"/>
        <circle cx="23" cy="23" r="11" fill="#FFD9D9"/>
      </svg>`

    if (!this.points || !this.mediaElement) {
      return
    }

    this.points.forEach((poi, index) => {
      const poiElement = document.createElement('button')
      poiElement.className = `hotspots__poi hotspots__dot ${index === 0 ? classes.active : ''}`
      poiElement.style.left = `${poi.x}%`
      poiElement.style.top = `${poi.y}%`
      poiElement.dataset.poiId = poi.id
      poiElement.innerHTML = pointIcon
      this.mediaElement.appendChild(poiElement)
      this.poiElements.push(poiElement)

    })
  }

  /**
   * Positions the content boxes on the media element.
   */
  positionBoxes() {
    if (!this.points || !this.mediaContent) {
      return
    }

    this.points.forEach(point => {
      const correspondingContent =
      Array.from(this.mediaContent).find(content => content.dataset.poiId === point.id)
      if (correspondingContent) {
        correspondingContent.style.left = `${point.box_x}%`
        correspondingContent.style.top = `${point.box_y}%`
      }
    })

    // remove the mobile class so that boxes are visible after positioning
    this.detailsWrapper.classList.remove(classes.mobile)

    this.drawLines()
  }

  /**
   * Draws lines connecting the POIs to the content boxes.
   */

  drawLines() {
    const ctx = this.canvas.getContext('2d')
    const containerWidth = this.container.clientWidth
    const containerHeight = this.container.clientHeight
    const mediaWidth = this.mediaElement.getBoundingClientRect().width
    const mediaHeight = this.mediaElement.getBoundingClientRect().height
    const lineLength = 25
    const poiHalfWidth = 20

    this.points.forEach(point => {
      const contentElement = document.querySelector(`[data-poi-id="${point.id}"]`)
      const boxWidth = contentElement.offsetWidth
      const boxHeight = contentElement.offsetHeight
      // 20% down from the top
      const adjustment = boxHeight * 0.20

      // Convert percentage to pixel
      const boxX = containerWidth * (point.box_x / 100)
      const boxY = containerHeight * (point.box_y / 100)
      const poiX = mediaWidth * (point.x / 100)
      const poiY = mediaHeight * (point.y / 100)

      // Right edge of the box
      const boxRightEdge = boxX + boxWidth
      // Left edge of the box
      const boxLeftEdge = boxX

      const boxCenterX = boxX + (boxWidth / 2)
      const onRightSide = boxCenterX > containerWidth / 2

      const startX = onRightSide ? boxLeftEdge : boxRightEdge
      const startY = boxY + adjustment
      const endX = onRightSide ? startX - lineLength : startX + lineLength
      const endY = startY

      ctx.strokeStyle = '#F5EFEF'

      // Start drawing from the box to the 25px extension
      ctx.beginPath()
      ctx.lineWidth = 1.5
      ctx.moveTo(startX, startY)
      ctx.lineTo(endX, endY)

      // Extending lines to the POI
      const finalX = ((containerWidth - mediaWidth) / 2) + poiHalfWidth + poiX
      const finalY = poiY + poiHalfWidth
      ctx.lineTo(finalX, finalY)
      ctx.stroke()
    })
  }

  /**
   * Updates the active state for POIs and content.
   * @param {string} poiId - The ID of the active POI.
   */
  updateActiveState(poiId) {
    // Update the active state for POIs
    this.poiElements.forEach(poi => {
      poi.classList.toggle(classes.active, poi.dataset.poiId === poiId)
    })

    // Update the active state for content
    this.mediaContent.forEach(content => {
      content.classList.toggle(classes.active, content.dataset.poiId === poiId)
    })
  }
}
